import React from 'react';
import { Tooltip } from 'primereact/tooltip';
import { useTranslation } from 'react-i18next';
import './ReportCover.scss';
import { formatCompactCount } from '../../utils/utils';
import { formatLongDate } from '../../helpers/date/formatLongDate';
import { brandLabel } from './brandLabel';
import { modelIcon } from './modelIcon';
import { modelLabel } from './modelLabel';

interface ReportCoverProps {
  brand: string;
  /** ISO-8601 z payloadu raportu. */
  generatedAt: string;
  promptsAnalyzed: number;
  /** Liczba odpowiedzi objętych raportem; Checker jej nie ma, więc kafel jest opcjonalny. */
  responsesAnalyzed?: number;
  /** Klucze modeli, które odpowiadały — kafel pokazuje przy liczbie ich logotypy. */
  models: string[];
  /**
   * Zakres, za jaki policzono raport (`YYYY-MM-DD – YYYY-MM-DD`). Checker go NIE ma — jest snapshotem
   * „stan na teraz"; pełny raport klienta liczy się z wybranego okresu, więc musi je pokazać.
   */
  periodLabel?: string;
  /** Pobranie PDF-a; przycisk znika, gdy nie ma czego pobierać. */
  onDownloadPdf?: () => void;
  isDownloading?: boolean;
}

/**
 * Okładka raportu. Bez `periodLabel` (Checker) pokazuje datę wygenerowania — jest snapshotem „stan na
 * teraz", a nie raportem za zakres dat; pełny raport klienta podaje tam swój okres.
 */
export const ReportCover: React.FC<ReportCoverProps> = ({
  brand,
  generatedAt,
  promptsAnalyzed,
  responsesAnalyzed,
  models,
  periodLabel,
  onDownloadPdf,
  isDownloading = false,
}) => {
  const { t, i18n } = useTranslation();
  const generatedLabel = formatLongDate(generatedAt, i18n.language);

  // Liczby kafli idą przez wspólny formatter — cztery cyfry pod rząd („2052") czyta się źle,
  // a raport ogląda klient.
  const stats = [
    { value: formatCompactCount(promptsAnalyzed), label: t('prompts'), icons: [] as string[] },
    { value: formatCompactCount(models.length), label: t('models'), icons: models },
    // Skala raportu: ile odpowiedzi modeli w ogóle objął. Bez liczby kafel się nie pojawia.
    ...(responsesAnalyzed
      ? [{ value: formatCompactCount(responsesAnalyzed), label: t('reportAllResponses'), icons: [] as string[] }]
      : []),
  ];

  return (
    <header className="report-cover">
      <div className="report-cover__bar">
        <div className="logo">
          <div className="logo-box" />
        </div>
        {onDownloadPdf && (
          <button
            type="button"
            className="btn btn--black report-cover__print"
            onClick={onDownloadPdf}
            disabled={isDownloading}
          >
            <span className="ico--download ico--xs" />
            {t('reportSavePdf')}
          </button>
        )}
      </div>

      <h1 className="report-cover__brand">{brandLabel(brand)}</h1>
      <p className="report-cover__subtitle">{t('reportCoverSubtitle')}</p>
      <p className="report-cover__date">{periodLabel ?? generatedLabel}</p>

      <div className="report-cover__stats">
        {stats.map((stat) => (
          <div key={stat.label} className="report-cover__stat">
            <div className="report-cover__stat-main">
              <span className="report-cover__stat-value">{stat.value}</span>
              <span className="report-cover__stat-label">{stat.label}</span>
            </div>

            {stat.icons.length > 0 && (
              // Ten sam stos logotypów co na liście projektów, tylko bez interakcji.
              <div className="model-stack model-stack--static">
                {/* Jeden Tooltip obsługuje wszystkie logotypy — treść bierze z `data-pr-tooltip`. */}
                <Tooltip target=".report-cover__model-tip" />
                {stat.icons.map((model) => {
                  const icon = modelIcon(model);
                  return icon ? (
                    <span
                      key={model}
                      className="model-stack__item report-cover__model-tip"
                      data-pr-tooltip={modelLabel(model)}
                    >
                      <span className={`${icon} ico--md`} />
                    </span>
                  ) : null;
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </header>
  );
};

export default ReportCover;
